import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  jsonLd?: object | object[];
}

const SITE_URL = "https://hyperhausla.com";
const DEFAULT_OG_IMAGE = "/og-image.jpg";

const SEO = ({
  title,
  description,
  canonical,
  ogImage,
  ogType = "website",
  noindex = false,
  jsonLd,
}: SEOProps) => {
  const { pathname } = useLocation();
  const fullTitle = title.includes("Hyper Haus") ? title : `${title} | Hyper Haus`;
  // Always produce a canonical URL — use explicit prop or derive from current path
  const canonicalPath = canonical ?? pathname;
  const canonicalUrl = `${SITE_URL}${canonicalPath === "/" ? "" : canonicalPath}`;
  const ogImageUrl = ogImage ? `${SITE_URL}${ogImage}` : `${SITE_URL}${DEFAULT_OG_IMAGE}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="Hyper Haus" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

// Reusable structured data – LocalBusiness + Organization combined
export const localBusinessSchema = [
  {
    "@context": "https://schema.org",
    "@type": "AutoRepair",
    "@id": "https://hyperhausla.com/#business",
    "name": "Hyper Haus",
    "alternateName": "Hyper Haus LA",
    "description": "Family-owned hypercar and supercar protection specialists with over 40 years of experience. PPF, ceramic coating, window tint, custom wraps, and detailing — HyperCar Craftsmanship for All.",
    "url": "https://hyperhausla.com",
    "telephone": "+13109169046",
    "email": "biz@hyperhausla.com",
    "logo": "https://hyperhausla.com/lovable-uploads/ba0c482b-09e4-442e-8823-ed3cb6e4ade8.png",
    "image": [
      "https://hyperhausla.com/lovable-uploads/ba0c482b-09e4-442e-8823-ed3cb6e4ade8.png"
    ],
    "foundingDate": "1985",
    "founder": {
      "@type": "Person",
      "name": "Matt"
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "11934 S La Cienega Blvd",
      "addressLocality": "Hawthorne",
      "addressRegion": "CA",
      "postalCode": "90250",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 33.9164,
      "longitude": -118.3717
    },
    "areaServed": [
      { "@type": "City", "name": "Los Angeles" },
      { "@type": "City", "name": "Hawthorne" },
      { "@type": "City", "name": "Beverly Hills" },
      { "@type": "City", "name": "Inglewood" },
      { "@type": "City", "name": "Santa Monica" }
    ],
    "priceRange": "$$$",
    "currenciesAccepted": "USD",
    "paymentAccepted": "Cash, Credit Card",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "10:00",
        "closes": "16:00"
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Automotive Protection Services",
      "itemListElement": [
        { "@type": "OfferCatalog", "name": "Paint Protection Film (PPF)" },
        { "@type": "OfferCatalog", "name": "Ceramic Coating" },
        { "@type": "OfferCatalog", "name": "Window Tinting" },
        { "@type": "OfferCatalog", "name": "Custom Vehicle Wraps" },
        { "@type": "OfferCatalog", "name": "Auto Detailing" },
        { "@type": "OfferCatalog", "name": "Commercial Window Tinting" },
        { "@type": "OfferCatalog", "name": "Marine PPF" }
      ]
    },
    "sameAs": [
      "https://www.instagram.com/hyper.haus",
      "https://www.yelp.com/biz/hyper-haus-hawthorne",
      "https://www.google.com/maps/place/Hyper+Haus"
    ],
    "knowsAbout": [
      "Paint Protection Film",
      "Ceramic Coating",
      "Window Tinting",
      "Vehicle Wraps",
      "Auto Detailing",
      "MiracleCoat PPF",
      "Pagani",
      "Koenigsegg",
      "Bugatti",
      "McLaren",
      "Porsche"
    ],
    "slogan": "HyperCar Craftsmanship for All"
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://hyperhausla.com/#organization",
    "name": "Hyper Haus",
    "url": "https://hyperhausla.com",
    "logo": {
      "@type": "ImageObject",
      "url": "https://hyperhausla.com/lovable-uploads/ba0c482b-09e4-442e-8823-ed3cb6e4ade8.png",
      "width": 600,
      "height": 600
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+13109169046",
      "contactType": "customer service",
      "email": "biz@hyperhausla.com",
      "areaServed": "US",
      "availableLanguage": ["English", "Spanish"]
    },
    "sameAs": [
      "https://www.instagram.com/hyper.haus",
      "https://www.yelp.com/biz/hyper-haus-hawthorne",
      "https://www.google.com/maps/place/Hyper+Haus"
    ]
  }
];
